// Visitor Management — the site visitor log plus gate check-in / check-out.
//
// Layout: a left list of visitors (filterable by site, searchable by
// name/company) and a right detail pane for the selected visitor. The detail
// pane shows the visitor's particulars, quick gate actions (check-in,
// check-out, denied, escort start/end) and the append-only access-event log.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  Visitor,
  AccessEvent,
  AccessEventType,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import { ApiError } from '../api/client';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Select,
  Table,
  type BadgeTone,
  type SelectOption,
} from '../components/ui';
import {
  useVisitors,
  useCreateVisitor,
  useUpdateVisitor,
  useDeleteVisitor,
  useAccessEvents,
  useCreateAccessEvent,
  useSitesLookup,
  type VisitorFilters,
} from '../api/queries/visitors';

// ---------- Static option lists ----------

const EVENT_TYPES: { value: AccessEventType; label: string }[] = [
  { value: 'check_in', label: 'Check in' },
  { value: 'check_out', label: 'Check out' },
  { value: 'denied', label: 'Denied' },
  { value: 'escort_start', label: 'Escort start' },
  { value: 'escort_end', label: 'Escort end' },
];

function eventLabel(t: AccessEventType): string {
  return EVENT_TYPES.find((x) => x.value === t)?.label ?? t;
}

// Access-event type → shared Badge tone (mirrors the old badge-* mapping).
function eventTone(t: AccessEventType): BadgeTone {
  switch (t) {
    case 'check_in':
      return 'success';
    case 'check_out':
      return 'neutral';
    case 'denied':
      return 'danger';
    case 'escort_start':
    case 'escort_end':
      return 'warn';
    default:
      return 'neutral';
  }
}

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number): string {
  return new Date(epochMs).toLocaleString();
}

// Derive a coarse on-site status from the latest access event, so the log can
// surface who is currently inside without a dedicated server field.
function onSiteStatus(latest: AccessEvent | undefined): 'on_site' | 'off_site' | 'unknown' {
  if (!latest) return 'unknown';
  if (latest.event_type === 'check_in' || latest.event_type === 'escort_start') {
    return 'on_site';
  }
  if (latest.event_type === 'check_out' || latest.event_type === 'denied') {
    return 'off_site';
  }
  return 'unknown';
}

// =============================================================================
// Page
// =============================================================================

export function VisitorsPage(): JSX.Element {
  const canWrite = can('visitor.write');

  const [filters, setFilters] = useState<VisitorFilters>({});
  const [searchText, setSearchText] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Visitor | null>(null);
  const [deleting, setDeleting] = useState<Visitor | null>(null);

  // Debounce the free-text search into the query filters.
  useEffect(() => {
    const h = setTimeout(() => {
      setFilters((f) => ({ ...f, q: searchText.trim() ? searchText.trim() : undefined }));
    }, 300);
    return () => clearTimeout(h);
  }, [searchText]);

  const visitorsQuery = useVisitors(filters);
  const sitesQuery = useSitesLookup();
  const deleteVisitor = useDeleteVisitor();

  const visitors = visitorsQuery.data?.visitors ?? [];
  const sites = sitesQuery.data?.sites ?? [];
  const siteName = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sites) map.set(s.id, s.name);
    return (id: number): string => map.get(id) ?? `Site #${id}`;
  }, [sites]);

  const siteFilterOptions: SelectOption[] = useMemo(
    () => [
      { value: 'all', label: 'All sites' },
      ...sites.map((s) => ({ value: String(s.id), label: s.name })),
    ],
    [sites],
  );

  const selected = useMemo(
    () => visitors.find((v) => v.id === selectedId) ?? null,
    [visitors, selectedId],
  );

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteVisitor.mutateAsync(deleting.id);
      toast.success(`Removed visitor “${deleting.full_name}”.`);
      if (selectedId === deleting.id) setSelectedId(null);
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to remove visitor.'));
    }
  };

  return (
    <div className="visitors-page">
      <header className="page-header">
        <div>
          <h2>Visitors</h2>
          <p className="page-sub">
            Site visitor log with gate check-in, check-out and escort tracking.
          </p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Log visitor
          </Button>
        )}
      </header>

      <div className="page-toolbar">
        <Select
          value={filters.site_id !== undefined ? String(filters.site_id) : 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              site_id: v === 'all' ? undefined : Number(v),
            }))
          }
          options={siteFilterOptions}
          aria-label="Filter by site"
        />

        <label className="form-field inline">
          <span>Search</span>
          <Input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Name or company"
          />
        </label>
      </div>

      <div className="split-layout">
        <section className="split-list" aria-label="Visitors">
          {visitorsQuery.isLoading && <p className="muted">Loading visitors…</p>}
          {visitorsQuery.isError && (
            <p className="error-text" role="alert">
              {errMessage(visitorsQuery.error, 'Failed to load visitors.')}
            </p>
          )}
          {!visitorsQuery.isLoading && visitors.length === 0 && (
            <p className="muted empty-state">No visitors match these filters.</p>
          )}

          <ul className="list-cards">
            {visitors.map((v) => (
              <li key={v.id}>
                <button
                  type="button"
                  className={`list-card ${selectedId === v.id ? 'active' : ''}`}
                  onClick={() => setSelectedId(v.id)}
                  aria-pressed={selectedId === v.id}
                >
                  <div className="list-card-main">
                    <span className="list-card-title">{v.full_name}</span>
                    {v.badge_no && <span className="badge">#{v.badge_no}</span>}
                  </div>
                  <div className="list-card-meta">
                    <span className="list-card-desc">{siteName(v.site_id)}</span>
                    {v.company && <span className="badge badge-muted">{v.company}</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="split-detail" aria-label="Visitor detail">
          {!selected ? (
            <div className="detail-placeholder muted">
              Select a visitor to view their gate log and record check-in / check-out.
            </div>
          ) : (
            <VisitorDetail
              key={selected.id}
              visitor={selected}
              siteName={siteName(selected.site_id)}
              canWrite={canWrite}
              onEdit={() => setEditing(selected)}
              onDelete={() => setDeleting(selected)}
            />
          )}
        </section>
      </div>

      {/* Create visitor */}
      {createOpen && (
        <VisitorFormModal
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={(v) => {
            setCreateOpen(false);
            setSelectedId(v.id);
          }}
        />
      )}

      {/* Edit visitor */}
      {editing && (
        <VisitorFormModal
          mode="edit"
          visitor={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}

      {/* Remove visitor */}
      <ConfirmDialog
        open={deleting !== null}
        title="Remove visitor"
        message={
          deleting
            ? `Remove “${deleting.full_name}” from the visitor log? The gate event history is preserved.`
            : ''
        }
        confirmLabel="Remove"
        danger
        busy={deleteVisitor.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

// =============================================================================
// Visitor create/edit modal
// =============================================================================

interface VisitorFormModalProps {
  mode: 'create' | 'edit';
  visitor?: Visitor;
  onClose: () => void;
  onSaved: (v: Visitor) => void;
}

function VisitorFormModal({
  mode,
  visitor,
  onClose,
  onSaved,
}: VisitorFormModalProps): JSX.Element {
  const create = useCreateVisitor();
  const update = useUpdateVisitor();
  const sitesQuery = useSitesLookup();
  const busy = create.isPending || update.isPending;
  const sites = sitesQuery.data?.sites ?? [];

  const [fullName, setFullName] = useState(visitor?.full_name ?? '');
  const [siteId, setSiteId] = useState<number | ''>(visitor?.site_id ?? '');
  const [company, setCompany] = useState(visitor?.company ?? '');
  const [hostContact, setHostContact] = useState(visitor?.host_contact ?? '');
  const [badgeNo, setBadgeNo] = useState(visitor?.badge_no ?? '');
  const [vehiclePlate, setVehiclePlate] = useState(visitor?.vehicle_plate ?? '');

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const name = fullName.trim();
    if (!name) {
      toast.warn('Full name is required.');
      return;
    }
    const companyV = company.trim();
    const hostV = hostContact.trim();
    const badgeV = badgeNo.trim();
    const plateV = vehiclePlate.trim();
    try {
      if (mode === 'create') {
        if (siteId === '') {
          toast.warn('Site is required.');
          return;
        }
        const res = await create.mutateAsync({
          site_id: siteId,
          full_name: name,
          ...(companyV ? { company: companyV } : {}),
          ...(hostV ? { host_contact: hostV } : {}),
          ...(badgeV ? { badge_no: badgeV } : {}),
          ...(plateV ? { vehicle_plate: plateV } : {}),
        });
        toast.success(`Logged visitor “${res.visitor.full_name}”.`);
        onSaved(res.visitor);
      } else if (visitor) {
        const res = await update.mutateAsync({
          id: visitor.id,
          full_name: name,
          company: companyV ? companyV : null,
          host_contact: hostV ? hostV : null,
          badge_no: badgeV ? badgeV : null,
          vehicle_plate: plateV ? plateV : null,
        });
        toast.success('Visitor updated.');
        onSaved(res.visitor);
      }
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to save visitor.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'Log visitor' : 'Edit visitor'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="visitor-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Log visitor' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="visitor-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="Full name" required>
          <Input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            maxLength={200}
            required
            autoFocus
          />
        </FormField>

        {mode === 'create' && (
          <FormField label="Site" required>
            <select
              className="form-input"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')}
              required
            >
              <option value="">Select a site…</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
        )}

        <FormRow>
          <FormField label="Company">
            <Input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              maxLength={200}
              placeholder="Optional"
            />
          </FormField>
          <FormField label="Host contact">
            <Input
              type="text"
              value={hostContact}
              onChange={(e) => setHostContact(e.target.value)}
              maxLength={200}
              placeholder="Optional"
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Badge no.">
            <Input
              type="text"
              value={badgeNo}
              onChange={(e) => setBadgeNo(e.target.value)}
              maxLength={80}
              placeholder="Optional"
            />
          </FormField>
          <FormField label="Vehicle plate">
            <Input
              type="text"
              value={vehiclePlate}
              onChange={(e) => setVehiclePlate(e.target.value)}
              maxLength={40}
              placeholder="Optional"
            />
          </FormField>
        </FormRow>
      </form>
    </Dialog>
  );
}

// =============================================================================
// Visitor detail — gate actions + access-event log
// =============================================================================

interface VisitorDetailProps {
  visitor: Visitor;
  siteName: string;
  canWrite: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function VisitorDetail({
  visitor,
  siteName,
  canWrite,
  onEdit,
  onDelete,
}: VisitorDetailProps): JSX.Element {
  const eventsQuery = useAccessEvents(visitor.id);
  const createEvent = useCreateAccessEvent();

  const events = eventsQuery.data?.access_events ?? [];
  // List is returned newest-first; the first entry is the latest event.
  const latest = events[0];
  const status = onSiteStatus(latest);

  const [gate, setGate] = useState('');

  const logEvent = async (eventType: AccessEventType): Promise<void> => {
    const gateV = gate.trim();
    try {
      await createEvent.mutateAsync({
        visitorId: visitor.id,
        event_type: eventType,
        ...(gateV ? { gate: gateV } : {}),
      });
      toast.success(`${eventLabel(eventType)} recorded.`);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to record event.'));
    }
  };

  return (
    <div className="visitor-detail">
      <header className="detail-header">
        <div>
          <h3>{visitor.full_name}</h3>
          <div className="detail-subline">
            <span className="badge">{siteName}</span>
            {status === 'on_site' && <span className="badge badge-success">On site</span>}
            {status === 'off_site' && <span className="badge badge-muted">Off site</span>}
            {visitor.company && <span className="badge badge-muted">{visitor.company}</span>}
            {visitor.badge_no && <span className="badge">Badge #{visitor.badge_no}</span>}
          </div>
          <dl className="detail-fields">
            {visitor.host_contact && (
              <div>
                <dt>Host</dt>
                <dd>{visitor.host_contact}</dd>
              </div>
            )}
            {visitor.vehicle_plate && (
              <div>
                <dt>Vehicle</dt>
                <dd>{visitor.vehicle_plate}</dd>
              </div>
            )}
          </dl>
        </div>
        {canWrite && (
          <div className="detail-actions">
            <Button variant="ghost" onClick={onEdit}>
              Edit
            </Button>
            <Button variant="danger" onClick={onDelete}>
              Remove
            </Button>
          </div>
        )}
      </header>

      {/* Gate actions */}
      {canWrite && (
        <div className="detail-section">
          <div className="detail-section-head">
            <h4>Gate actions</h4>
            <label className="form-field inline">
              <span>Gate</span>
              <Input
                type="text"
                value={gate}
                onChange={(e) => setGate(e.target.value)}
                maxLength={120}
                placeholder="e.g. Main lobby"
              />
            </label>
          </div>
          <div className="detail-actions gate-actions">
            <Button
              variant="primary"
              onClick={() => void logEvent('check_in')}
              disabled={createEvent.isPending}
            >
              Check in
            </Button>
            {/* No "secondary" Button variant yet — keep the native class. */}
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void logEvent('check_out')}
              disabled={createEvent.isPending}
            >
              Check out
            </button>
            <Button
              variant="ghost"
              onClick={() => void logEvent('escort_start')}
              disabled={createEvent.isPending}
            >
              Escort start
            </Button>
            <Button
              variant="ghost"
              onClick={() => void logEvent('escort_end')}
              disabled={createEvent.isPending}
            >
              Escort end
            </Button>
            <Button
              variant="danger"
              onClick={() => void logEvent('denied')}
              disabled={createEvent.isPending}
            >
              Deny entry
            </Button>
          </div>
        </div>
      )}

      {/* Access-event log */}
      <div className="detail-section">
        <h4>Access log</h4>
        {eventsQuery.isLoading && <p className="muted">Loading access log…</p>}
        {eventsQuery.isError && (
          <p className="error-text" role="alert">
            {errMessage(eventsQuery.error, 'Failed to load access log.')}
          </p>
        )}
        {!eventsQuery.isLoading && events.length === 0 && (
          <p className="muted empty-state">No gate events recorded yet.</p>
        )}

        {events.length > 0 && (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>When</Table.HeaderCell>
                <Table.HeaderCell>Event</Table.HeaderCell>
                <Table.HeaderCell>Gate</Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {events.map((ev) => (
                <Table.Row key={ev.id}>
                  <Table.Cell>{fmtDate(ev.event_at)}</Table.Cell>
                  <Table.Cell>
                    <Badge tone={eventTone(ev.event_type)}>
                      {eventLabel(ev.event_type)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{ev.gate ?? '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  );
}
